import { Accordion } from "@/components/ui/accordion";
import { useAuth } from "@/context/auth/AuthContext";
import { SupportedLocale } from "@/i18n";
import { Multipleprint, Multiplesample } from "@/lib/types";
import { HistoryAccordion } from "@/pages/dashboard/artwork-overview/accordions/HistoryAccordion";
import { ComputedFieldsMultipleprint } from "../types";
import { AuctionsAccordion } from "./AuctionsAccordion";
import { BibliographyAccordion } from "./BibliographyAccordion";
import { CollectionsPubliquesAccordion } from "./CollectionsPubliquesAccordion";
import { CreditsAccordion } from "./CreditsAccordion";
import { DescriptionEpreuvesTirageAccordion } from "./DescriptionEpreuvesTirageAccordion";
import { ExemplaireAccordions } from "./ExemplaireAccordions";
import { ExemplairesAccordion } from "./ExemplairesAccordion";
import { ExhibitionsAccordion } from "./ExhibitionsAccordion";
import { RelatedWorksAccordion } from "./RelatedWorksAccordion";

export {
  ACCORDION_LIST_CONTENT_CLASS,
  AccordionClass,
} from "@/lib/accordion-content";

interface MultipleprintAccordionsProps {
  data: Multipleprint;
  computedFields: ComputedFieldsMultipleprint;
  locale: SupportedLocale;
  t: (key: string, options?: Record<string, unknown>) => string;
  itemType?: "MULTIPLE" | "EXEMPLAIRE";
  selectedSample?: Multiplesample | null;
}

export function MultipleprintAccordions({
  data,
  computedFields,
  locale,
  t,
  itemType = "MULTIPLE",
  selectedSample = null,
}: MultipleprintAccordionsProps) {
  const { instanceData } = useAuth();

  if (itemType === "EXEMPLAIRE" && selectedSample) {
    return (
      <ExemplaireAccordions
        data={data}
        selectedSample={selectedSample}
        locale={locale}
        t={t}
      />
    );
  }

  const provenanceMode = instanceData?.provenanceMode ?? false;

  const descriptionHtml =
    data.estampesMultiplesDescriptionDescriptionCompleteMultilingual?.[
      locale
    ] ??
    data.estampesMultiplesDescriptionDescriptionComplete ??
    "";

  return (
    <Accordion
      type="multiple"
      className="font-normal text-base text-gray-800 mb-32"
    >
      <HistoryAccordion
        historyCalculated={computedFields.historyCalculated}
        tirageHistoryCalculated={null}
        t={t}
        title={
          provenanceMode
            ? t("multipleprint.accordion.provenance.title")
            : t("multipleprint.accordion.history.title")
        }
      />
      <DescriptionEpreuvesTirageAccordion
        descriptionHtml={descriptionHtml}
        t={t}
      />
      <ExhibitionsAccordion
        exhibitionsCalculated={computedFields.exhibitionsCalculated}
        t={t}
      />
      <BibliographyAccordion
        bibliographyCalculated={computedFields.bibliographyCalculated}
        t={t}
      />
      {!provenanceMode && (
        <AuctionsAccordion
          auctionCalculated={computedFields.auctionCalculated}
          t={t}
        />
      )}
      <CreditsAccordion
        creditOeuvres={data.estampesMultiplesPhotosCreditOeuvres ?? ""}
        creditPhotoOeuvres={
          data.estampesMultiplesPhotosCreditPhotoOeuvres ?? ""
        }
        creditRemarques={
          data.estampesMultiplesPhotosCreditRemarques ?? undefined
        }
        locale={locale}
        t={t}
      />
      <RelatedWorksAccordion data={data} locale={locale} t={t} />
      <ExemplairesAccordion
        data={data}
        exemplaires={data.relatedMultiplesample ?? []}
        locale={locale}
        t={t}
      />
      <CollectionsPubliquesAccordion
        exemplaires={data.relatedMultiplesample ?? []}
        locale={locale}
        t={t}
        translation={t}
      />
    </Accordion>
  );
}
